import React from 'react';
import { Spin, Alert, Typography, Collapse, List as AntList, Tag } from 'antd';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';

const { Text } = Typography;
const { Panel } = Collapse;

const NutritionParameterSummary = ({ factors = [], loading: externalLoading = false, nutritionData = null }) => {
  // Handle different usage patterns - either factors array or nutritionData object
  const effectiveFactors = factors.length > 0 ? factors : (nutritionData?.contributingParams || []);

  // Function to classify parameters by health status
  const classifyParametersByStatus = (parameters) => {
    const statusGroups = {
      allClear: [],
      areasToWatch: [],
      actionRecommended: []
    };

    parameters.forEach(param => {
      // Classification logic based on z-score, priority, and health significance
      const zScore = Math.abs(param.zScore || 0);
      const priority = param.priority || 'Low';
      const healthScore = param.healthScore || 0;

      // Action Recommended: High priority OR significant z-score deviation OR very low health score
      if (priority === 'High' || zScore > 2.0 || healthScore < 40) {
        statusGroups.actionRecommended.push(param);
      }
      // Areas to Watch: Moderate priority OR moderate z-score deviation OR moderate health score
      else if (priority === 'Moderate' || zScore > 1.0 || (healthScore >= 40 && healthScore < 70)) {
        statusGroups.areasToWatch.push(param);
      }
      // All Clear: Low priority AND low z-score deviation AND good health score
      else {
        statusGroups.allClear.push(param);
      }
    });

    return statusGroups;
  };

  if (externalLoading) {
    return <Spin tip="Loading..." />;
  }

  // If no factors to display, show a message
  if (effectiveFactors.length === 0) {
    return <Alert message="No nutrition factors to display" type="info" />;
  }

  // Classify parameters by health status
  const statusGroups = classifyParametersByStatus(effectiveFactors);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'red';
      case 'Moderate': return 'orange';
      case 'Low': return 'blue';
      default: return 'grey';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'allClear': return '#52c41a'; // Green
      case 'areasToWatch': return '#faad14'; // Orange
      case 'actionRecommended': return '#f5222d'; // Red
      default: return '#d9d9d9';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'allClear': return '✅';
      case 'areasToWatch': return '⚠️';
      case 'actionRecommended': return '🚨';
      default: return '';
    }
  };

  // Helper function to get parameter display name with fallback
  const getParameterDisplayNameWithFallback = (paramKey, fallbackName) => {
    try {
      return getParameterDisplayName(paramKey);
    } catch (error) {
      console.warn(`[NutritionParameterSummary] Parameter ${paramKey} not found in centralized display names, using fallback: ${fallbackName}`);
      return fallbackName || paramKey;
    }
  };

  const renderParameterList = (parameters) => (
    <AntList
      itemLayout="vertical"
      dataSource={parameters}
      renderItem={item => (
        <AntList.Item key={item.key}>
          <AntList.Item.Meta
            title={<><Tag color={getPriorityColor(item.priority)}>{item.priority}</Tag>{getParameterDisplayNameWithFallback(item.key, item.name)}</>}
            description={
              <Text>
                Z-Score: {item.zScore?.toFixed(2)}σ | Impact: {item.specificImpact?.toFixed(3)}
              </Text>
            }
          />
        </AntList.Item>
      )}
    />
  );

  return (
    <div>
      {/* Total Parameters Count */}
      <div style={{ marginBottom: '16px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
        <Text strong>Total Parameters: {effectiveFactors.length}</Text>
      </div>

      <Collapse defaultActiveKey={['allClear', 'areasToWatch', 'actionRecommended']}>
        {/* All Clear Section */}
        {statusGroups.allClear.length > 0 && (
          <Panel
            header={
              <span style={{ color: getStatusColor('allClear') }}>
                {getStatusIcon('allClear')} All Clear ({statusGroups.allClear.length})
              </span>
            }
            key="allClear"
          >
            {renderParameterList(statusGroups.allClear)}
          </Panel>
        )}

        {/* Areas to Watch Section */}
        {statusGroups.areasToWatch.length > 0 && (
          <Panel
            header={
              <span style={{ color: getStatusColor('areasToWatch') }}>
                {getStatusIcon('areasToWatch')} Areas to Watch ({statusGroups.areasToWatch.length})
              </span>
            }
            key="areasToWatch"
          >
            {renderParameterList(statusGroups.areasToWatch)}
          </Panel>
        )}

        {/* Action Recommended Section */}
        {statusGroups.actionRecommended.length > 0 && (
          <Panel
            header={
              <span style={{ color: getStatusColor('actionRecommended') }}>
                {getStatusIcon('actionRecommended')} Action Recommended ({statusGroups.actionRecommended.length})
              </span>
            }
            key="actionRecommended"
          >
            {renderParameterList(statusGroups.actionRecommended)}
          </Panel>
        )}
      </Collapse>
    </div>
  );
};

export default NutritionParameterSummary;
